import React, { useCallback, useContext, useEffect, useState, useMemo } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import {
  Paper,
  Grid,
  Tooltip,
  IconButton,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Collapse,
  Box,
  Typography,
  Skeleton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SearchIcon from '@mui/icons-material/Search';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import styles from './styles.module.css';
import CustomTablePagination from 'components/utils/Pagination/CustomTablePagination';
import { WarehouseContext } from 'Context/WarehouseContext';
import { InventoryContext } from 'Context/InventoryContext';
import { ShopContext } from 'Context/ShopContext';
import { useTranslation } from 'react-i18next';
import withConsoleBase from 'components/utils/ConsoleBase/withConsoleBase';
import ShopsHelper from 'helpers/ShopsHelper';
import { ROUTES } from 'const';
import WarehouseService from 'services/WarehouseService';

function WarehouseStockRow({ row, shop, stockBatches, warehouseId, onEdit }) {
  const { t: tCommon } = useTranslation('common');
  const [open, setOpen] = useState(false);
  const history = useHistory();

  const hasBatches = useMemo(() => {
    if (!Array.isArray(stockBatches)) return false;
    const itemId = String(row.item?.id || row.product?.id || row.id);
    return stockBatches.some((b) => String(b.item) === itemId);
  }, [stockBatches, row]);

  const isBatchEnabled = !!(
    row.item?.is_batch_enabled ||
    row.product?.is_batch_enabled ||
    row.is_batch_enabled ||
    hasBatches
  );

  const itemBatches = useMemo(() => {
    if (!isBatchEnabled || !Array.isArray(stockBatches)) return [];
    const itemId = String(row.item?.id || row.product?.id || row.id);
    return stockBatches.filter((b) => String(b.warehouse) === String(warehouseId) && String(b.item) === itemId);
  }, [isBatchEnabled, stockBatches, row, warehouseId]);

  return (
    <>
      <TableRow hover sx={{ '& > *': { borderBottom: open ? 'none' : '1px solid rgba(224, 224, 224, 1)' } }}>
        <TableCell width="50px" style={{ padding: '4px' }}>
          {isBatchEnabled && (
            <IconButton aria-label="expand row" size="small" onClick={() => setOpen(!open)}>
              {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
            </IconButton>
          )}
        </TableCell>
        <TableCell>{row.item?.plu || row.plu || row.product?.plu || '-'}</TableCell>
        <TableCell>{row.item?.name || row.name || row.product?.name || '-'}</TableCell>
        <TableCell>{row.item?.unit || row.unit || row.product?.unit || '-'}</TableCell>
        <TableCell>
          {ShopsHelper.getAmountFormatted(shop, row.item?.price || row.price || row.product?.price || 0)}
        </TableCell>
        <TableCell>{row.stock ?? 0}</TableCell>
        <TableCell>
          <Tooltip title={tCommon('Details')}>
            <IconButton
              size="small"
              onClick={() => {
                const itemId = row.item?.id || row.product?.id || row.id;
                history.push(ROUTES.ITEM_DETAILS.replace(':itemId', itemId));
              }}
              style={{ marginRight: '8px' }}
            >
              <VisibilityIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={tCommon('Edit')}>
            <IconButton size="small" onClick={() => onEdit(row)}>
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </TableCell>
      </TableRow>
      {isBatchEnabled && (
        <TableRow>
          <TableCell
            style={{
              paddingBottom: open ? '16px' : 0,
              paddingTop: 0,
              borderBottom: open ? '1px solid rgba(224, 224, 224, 1)' : 'none',
            }}
            colSpan={7}
          >
            <Collapse in={open} timeout="auto" unmountOnExit>
              <Box
                sx={{
                  margin: '8px 16px 16px 16px',
                  padding: 2,
                  bgcolor: 'rgba(0, 0, 0, 0.02)',
                  borderRadius: '8px',
                  border: '1px solid rgba(0, 0, 0, 0.05)',
                }}
              >
                <Typography
                  variant="subtitle2"
                  gutterBottom
                  component="div"
                  sx={{ fontWeight: 'bold', color: '#1d57ac', marginBottom: '10px' }}
                >
                  {tCommon('Batches')}
                </Typography>
                <Table size="small" aria-label="batches">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold', color: '#555' }}>{tCommon('Batch Name')}</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', color: '#555' }}>{tCommon('Price')}</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', color: '#555' }}>{tCommon('Stock')}</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', color: '#555' }}>{tCommon('Expiry Date')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {itemBatches.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={4} align="center" style={{ color: '#888', padding: '16px' }}>
                          {tCommon('No batches found')}
                        </TableCell>
                      </TableRow>
                    ) : (
                      itemBatches.map((batch) => (
                        <TableRow key={batch.id}>
                          <TableCell>{batch.batch_name || batch.batch_no || '-'}</TableCell>
                          <TableCell>
                            {ShopsHelper.getAmountFormatted(shop, batch.price ?? 0)}
                          </TableCell>
                          <TableCell>{batch.stock ?? 0}</TableCell>
                          <TableCell>
                            {batch.expiry_date ? new Date(batch.expiry_date).toLocaleDateString() : 'N/A'}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </Box>
            </Collapse>
          </TableCell>
        </TableRow>
      )}
    </>
  );
}

function WarehouseStock() {
  const { t: tCommon } = useTranslation('common');
  const { warehouseId } = useParams();
  const history = useHistory();
  const { shop } = useContext(ShopContext);
  const { getWarehouseProducts, warehouseProducts, warehouses, getWarehouses, importDefaultStock } =
    useContext(WarehouseContext);
  const { getStockBatches, stockBatches } = useContext(InventoryContext);

  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);

  const handleImportDefaultStock = useCallback(async () => {
    setImporting(true);
    try {
      await importDefaultStock(warehouseId);
      const latestProducts = await getWarehouseProducts({ useCache: true });
      const currentWarehouseProducts = latestProducts.filter((p) => String(p.warehouse?.id) === String(warehouseId));
      const hasBatchEnabled = currentWarehouseProducts.some((p) => p.item?.is_batch_enabled);
      if (hasBatchEnabled) {
        await getStockBatches({ useCache: false });
      }
    } catch (e) {
      console.error(e);
    } finally {
      setImporting(false);
    }
  }, [importDefaultStock, warehouseId, getWarehouseProducts, getStockBatches]);
  const [searchText, setSearchText] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [filteredProducts, setFilteredProducts] = useState([]);

  const [editingRow, setEditingRow] = useState(null);
  const [editPopupOpen, setEditPopupOpen] = useState(false);
  const [action, setAction] = useState('add');
  const [quantity, setQuantity] = useState('');
  const [selectedBatchId, setSelectedBatchId] = useState('');
  const [currentStock, setCurrentStock] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [newBatchName, setNewBatchName] = useState('');
  const [expiryDate, setExpiryDate] = useState('');

  const isEditingRowBatchEnabled = useMemo(() => {
    if (!editingRow) return false;
    const hasBatches =
      Array.isArray(stockBatches) &&
      stockBatches.some(
        (b) => String(b.item) === String(editingRow.item?.id || editingRow.product?.id || editingRow.id)
      );
    return !!(
      editingRow.item?.is_batch_enabled ||
      editingRow.product?.is_batch_enabled ||
      editingRow.is_batch_enabled ||
      hasBatches
    );
  }, [editingRow, stockBatches]);

  const editingItemBatches = useMemo(() => {
    if (!editingRow || !Array.isArray(stockBatches)) return [];
    const itemId = String(editingRow.item?.id || editingRow.product?.id || editingRow.id);
    return stockBatches.filter((b) => String(b.warehouse) === String(warehouseId) && String(b.item) === itemId);
  }, [editingRow, stockBatches, warehouseId]);

  const handleOpenEditPopup = (row) => {
    setEditingRow(row);
    setAction('add');
    setQuantity('');
    setErrorMsg('');
    setNewBatchName('');
    setExpiryDate('');

    const hasBatches =
      Array.isArray(stockBatches) &&
      stockBatches.some((b) => String(b.item) === String(row.item?.id || row.product?.id || row.id));
    const isBatch = !!(
      row.item?.is_batch_enabled ||
      row.product?.is_batch_enabled ||
      row.is_batch_enabled ||
      hasBatches
    );

    if (isBatch) {
      const itemId = String(row.item?.id || row.product?.id || row.id);
      const batches = stockBatches.filter(
        (b) => String(b.warehouse) === String(warehouseId) && String(b.item) === itemId
      );
      if (batches.length > 0) {
        setSelectedBatchId(batches[0].id);
        setCurrentStock(batches[0].stock ?? 0);
      } else {
        setSelectedBatchId('new');
        setCurrentStock(0);
      }
    } else {
      setSelectedBatchId('');
      setCurrentStock(row.stock ?? 0);
    }
    setEditPopupOpen(true);
  };

  const handleCloseEditPopup = () => {
    setEditPopupOpen(false);
    setEditingRow(null);
  };

  const handleSubmitEdit = async () => {
    if (!editingRow) return;
    setSubmitting(true);
    setErrorMsg('');
    try {
      const qtyNum = parseFloat(quantity);
      if (isNaN(qtyNum) || qtyNum <= 0) {
        setErrorMsg('Please enter a valid positive quantity');
        setSubmitting(false);
        return;
      }

      const payload = {
        action,
        quantity: qtyNum,
      };

      if (isEditingRowBatchEnabled) {
        if (!selectedBatchId) {
          setErrorMsg('Please select a batch');
          setSubmitting(false);
          return;
        }
        payload.product_batch_id = selectedBatchId;
        if (selectedBatchId === 'new') {
          if (!newBatchName || !newBatchName.trim()) {
            setErrorMsg('Please enter a batch name');
            setSubmitting(false);
            return;
          }
          payload.batch_name = newBatchName.trim();
          payload.expiry_date = expiryDate || null;
        }
      }

      await WarehouseService.adjustWarehouseStock(editingRow.id, payload);
      await loadData();
      handleCloseEditPopup();
    } catch (err) {
      console.error(err);
      setErrorMsg(err.message || 'Failed to adjust stock');
    } finally {
      setSubmitting(false);
    }
  };

  const currentWarehouse = useMemo(
    () => warehouses.find((w) => String(w.id) === String(warehouseId)),
    [warehouses, warehouseId]
  );

  const products = useMemo(() => {
    if (!Array.isArray(warehouseProducts)) return [];
    return warehouseProducts.filter((p) => String(p.warehouse?.id) === String(warehouseId));
  }, [warehouseProducts, warehouseId]);

  const loadData = useCallback(async () => {
    setLoading(true);
    await Promise.all([getWarehouseProducts({ useCache: true }), getStockBatches({ useCache: true })]);
    setLoading(false);
  }, [getWarehouseProducts, getStockBatches]);

  useEffect(() => {
    if (!warehouses.length) {
      getWarehouses();
    }
  }, [warehouses.length, getWarehouses]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    const filtered = products.filter((p) => {
      const name = (p.item?.name || p.name || p.product?.name || '').toLowerCase();
      const plu = String(p.item?.plu || p.plu || p.product?.plu || '').toLowerCase();
      const search = searchText.toLowerCase();
      return name.includes(search) || plu.includes(search);
    });
    setFilteredProducts(filtered);
    setPage(0);
  }, [products, searchText]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const paginatedData = useMemo(() => {
    return filteredProducts.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  }, [filteredProducts, page, rowsPerPage]);

  return (
    <div className={styles.contentWrapper}>
      <div className={styles.titleSec}>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <IconButton onClick={() => history.goBack()} style={{ marginRight: '10px' }}>
            <ArrowBackIcon />
          </IconButton>
          <h2 className={styles.title}>
            {tCommon('Warehouse')}
            <span className={styles.menuTitle}>{tCommon('Stock')}</span>
            {currentWarehouse && <span className={styles.menuTitle}> - {currentWarehouse.name}</span>}
          </h2>
        </div>
      </div>

      <div className={styles.changeable}>
        <Grid container alignItems="center" spacing={2}>
          <Grid item xs={12} sm={6}>
            <h2 className={styles.subTitle}>{tCommon('Stock Details')}</h2>
          </Grid>
          <Grid item xs={12} sm={6}>
            <div className={styles.searchSec} style={{ float: 'right' }}>
              <input
                type="text"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                className={styles.searchInput}
                placeholder={tCommon('Search items')}
              />
              <Tooltip title={tCommon('Search')}>
                <IconButton size="small">
                  <SearchIcon />
                </IconButton>
              </Tooltip>
            </div>
          </Grid>
        </Grid>
      </div>

      {products.length === 0 && !loading && (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            flexDirection: 'column',
            padding: '40px',
            background: '#f9f9f9',
            borderRadius: '8px',
            marginBottom: '20px',
            border: '1px dashed #ccc',
          }}
        >
          {!warehouseProducts || warehouseProducts.length === 0 ? (
            <>
              <p style={{ margin: '0 0 20px 0', fontSize: '16px', color: '#555' }}>
                {tCommon('No products found in any warehouse. Please import default stock to initialize.')}
              </p>
              <Button
                variant="contained"
                color="primary"
                disabled={importing}
                onClick={handleImportDefaultStock}
                style={{ textTransform: 'none', fontWeight: 'bold' }}
              >
                {importing ? tCommon('Importing...') : tCommon('Import All Default Stock')}
              </Button>
            </>
          ) : (
            <p style={{ margin: '0', fontSize: '16px', color: '#555' }}>
              {tCommon('This warehouse currently has no items in stock.')}
            </p>
          )}
        </div>
      )}

      {(products.length > 0 || loading) && (
        <Paper className={styles.tableWrapper}>
          <TableContainer style={{ marginTop: '4px', overflowX: 'auto' }}>
            <Table aria-label="warehouse stock table">
              <TableHead style={{ backgroundColor: '#f8f9fa' }}>
                <TableRow>
                  <TableCell width="50px" style={{ borderBottom: '1px solid rgba(224, 224, 224, 1)' }} />
                  <TableCell
                    sx={{ fontWeight: 700, color: '#1d57ac', borderBottom: '1px solid rgba(224, 224, 224, 1)' }}
                  >
                    {tCommon('PLU')}
                  </TableCell>
                  <TableCell
                    sx={{ fontWeight: 700, color: '#1d57ac', borderBottom: '1px solid rgba(224, 224, 224, 1)' }}
                  >
                    {tCommon('Name')}
                  </TableCell>
                  <TableCell
                    sx={{ fontWeight: 700, color: '#1d57ac', borderBottom: '1px solid rgba(224, 224, 224, 1)' }}
                  >
                    {tCommon('Unit')}
                  </TableCell>
                  <TableCell
                    sx={{ fontWeight: 700, color: '#1d57ac', borderBottom: '1px solid rgba(224, 224, 224, 1)' }}
                  >
                    {tCommon('Price')}
                  </TableCell>
                  <TableCell
                    sx={{ fontWeight: 700, color: '#1d57ac', borderBottom: '1px solid rgba(224, 224, 224, 1)' }}
                  >
                    {tCommon('Stock')}
                  </TableCell>
                  <TableCell
                    sx={{ fontWeight: 700, color: '#1d57ac', borderBottom: '1px solid rgba(224, 224, 224, 1)' }}
                  >
                    {tCommon('Actions')}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading
                  ? [...Array(5)].map((_, index) => (
                      <TableRow key={`skeleton-${index}`}>
                        <TableCell />
                        <TableCell>
                          <Skeleton variant="text" width="80%" />
                        </TableCell>
                        <TableCell>
                          <Skeleton variant="text" width="80%" />
                        </TableCell>
                        <TableCell>
                          <Skeleton variant="text" width="80%" />
                        </TableCell>
                        <TableCell>
                          <Skeleton variant="text" width="80%" />
                        </TableCell>
                        <TableCell>
                          <Skeleton variant="text" width="80%" />
                        </TableCell>
                        <TableCell>
                          <Skeleton variant="text" width="80%" />
                        </TableCell>
                      </TableRow>
                    ))
                  : paginatedData.map((row) => (
                      <WarehouseStockRow
                        key={row.id}
                        row={row}
                        shop={shop}
                        stockBatches={stockBatches}
                        warehouseId={warehouseId}
                        onEdit={handleOpenEditPopup}
                      />
                    ))}
              </TableBody>
            </Table>
          </TableContainer>
          <CustomTablePagination
            count={filteredProducts.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Paper>
      )}

      {/* Edit Stock Dialog */}
      <Dialog open={editPopupOpen} onClose={handleCloseEditPopup} fullWidth maxWidth="xs">
        <DialogTitle sx={{ fontWeight: 'bold', color: '#1d57ac' }}>{tCommon('Edit Stock')}</DialogTitle>
        <DialogContent>
          {errorMsg && (
            <Alert severity="error" sx={{ marginBottom: 2 }}>
              {errorMsg}
            </Alert>
          )}
          <Typography variant="body1" sx={{ marginBottom: 2 }}>
            <strong>{tCommon('Item')}:</strong>{' '}
            {editingRow?.item?.name || editingRow?.name || editingRow?.product?.name || '-'}
          </Typography>

          {isEditingRowBatchEnabled ? (
            <FormControl fullWidth sx={{ marginBottom: 2 }}>
              <InputLabel id="select-batch-label">{tCommon('Select Batch')}</InputLabel>
              <Select
                labelId="select-batch-label"
                value={selectedBatchId}
                label={tCommon('Select Batch')}
                onChange={(e) => {
                  const bId = e.target.value;
                  setSelectedBatchId(bId);
                  if (bId === 'new') {
                    setCurrentStock(0);
                    setAction('add');
                  } else {
                    const selectedBatch = editingItemBatches.find((b) => String(b.id) === String(bId));
                    setCurrentStock(selectedBatch?.stock ?? 0);
                  }
                }}
              >
                {editingItemBatches.map((b) => (
                  <MenuItem key={b.id} value={b.id}>
                    {b.batch_name || b.batch_no || '-'} ({tCommon('Stock')}: {b.stock ?? 0})
                  </MenuItem>
                ))}
                <MenuItem value="new" sx={{ fontStyle: 'italic', color: '#1d57ac', fontWeight: 'bold' }}>
                  + {tCommon('Create New Batch...')}
                </MenuItem>
              </Select>
            </FormControl>
          ) : (
            <Typography variant="body2" sx={{ marginBottom: 2 }}>
              <strong>{tCommon('Current Stock')}:</strong> {editingRow?.stock ?? 0}
            </Typography>
          )}

          {isEditingRowBatchEnabled && selectedBatchId === 'new' && (
            <>
              <TextField
                fullWidth
                label={tCommon('New Batch Name')}
                value={newBatchName}
                onChange={(e) => setNewBatchName(e.target.value)}
                sx={{ marginBottom: 2 }}
                required
              />
              <TextField
                fullWidth
                type="date"
                label={tCommon('Expiry Date')}
                value={expiryDate}
                onChange={(e) => setExpiryDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{ marginBottom: 2 }}
              />
            </>
          )}

          {(!isEditingRowBatchEnabled || selectedBatchId) && (
            <>
              {isEditingRowBatchEnabled && selectedBatchId !== 'new' && (
                <Typography variant="body2" sx={{ marginBottom: 2 }}>
                  <strong>{tCommon('Current Batch Stock')}:</strong> {currentStock}
                </Typography>
              )}
              {selectedBatchId !== 'new' && (
                <FormControl fullWidth sx={{ marginBottom: 2 }}>
                  <InputLabel id="select-action-label">{tCommon('Action')}</InputLabel>
                  <Select
                    labelId="select-action-label"
                    value={action}
                    label={tCommon('Action')}
                    onChange={(e) => setAction(e.target.value)}
                  >
                    <MenuItem value="add">{tCommon('Add Stock')}</MenuItem>
                    <MenuItem value="remove">{tCommon('Remove Stock')}</MenuItem>
                  </Select>
                </FormControl>
              )}
              <TextField
                fullWidth
                type="number"
                label={selectedBatchId === 'new' ? tCommon('Quantity to Add') : tCommon('Quantity')}
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
                inputProps={{ min: 0, step: 'any' }}
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEditPopup} color="inherit">
            {tCommon('Cancel')}
          </Button>
          <Button
            onClick={handleSubmitEdit}
            color="primary"
            variant="contained"
            disabled={submitting || (isEditingRowBatchEnabled && !selectedBatchId) || !quantity}
          >
            {submitting ? tCommon('Saving...') : tCommon('Save')}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}

export default withConsoleBase(WarehouseStock);
